import { useHistory } from 'react-router-dom'
import { AlertTriangle, BadgeCheck, Clock, Lock, Network, Sparkles } from 'lucide-react'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useEntitlementStore } from '@caradasstore/EntitlementStore'
import { useUserStore } from '@caradasstore/UserStore'
import { entitlementUserKey } from '../../data/entitlementUser'
import { isTrialPackage, monthlyTiers, tierById } from '../../data/catalog'
import { useCatalog } from '../../store/CatalogStore'
import { daysUntil } from './billingView'

const BILLING_PATH = '/settings?section=billing'

// Tones for the dark header gradient: translucent fills, light text.
const TONES = {
  premium: 'border-white/25 bg-gradient-to-r from-[#3B82F6]/35 to-[#8B5CF6]/35 text-white shadow-[0_0_12px_rgba(99,102,241,0.35)]',
  plan: 'border-white/20 bg-white/10 text-white',
  trial: 'border-amber-300/40 bg-amber-400/15 text-amber-100',
  danger: 'border-red-300/40 bg-red-500/20 text-red-100',
  muted: 'border-white/15 bg-white/5 text-white/70'
}

const ICON_TONES = {
  premium: 'text-amber-300',
  plan: 'text-sky-300',
  trial: 'text-amber-300',
  danger: 'text-red-300',
  muted: 'text-white/60'
}

/**
 * What the active center's package badge says, or null while it isn't known for this center
 * (no flash of a wrong package on a center switch). The top monthly plan gets the premium look.
 */
export const packageBadgeOf = (entitlements, catalog, now = new Date()) => {
  const { tier, state, hasSubscription, trialPaused, managedExternally, trialEndDate } = entitlements
  if (managedExternally) return { label: 'CAR ADAS Network', tone: 'muted', Icon: Network, title: 'Billed through QuickBooks' }
  if (trialPaused) return { label: 'Trial paused', tone: 'danger', Icon: AlertTriangle, title: 'Trials are paused — subscribe to continue' }
  if (state === 'PAST_DUE')
    return { label: 'Payment failed', tone: 'danger', Icon: AlertTriangle, title: 'Paused until the payment goes through' }
  if (hasSubscription === false || !tier)
    return { label: 'No package', tone: 'muted', Icon: Lock, title: 'Choose a package to unlock the platform' }

  const plan = tierById(catalog, tier)
  const name = plan?.name ?? tier
  if (state === 'TRIAL') {
    const days = trialEndDate ? daysUntil(trialEndDate, now) : null
    const left = days === null ? '' : days === 1 ? ' · 1 day left' : ` · ${days} days left`
    const label = isTrialPackage(plan) ? `${name}${left}` : `${name} trial${left}`
    return {
      label,
      tone: 'trial',
      Icon: Clock,
      title: trialEndDate ? `Trial ends ${new Date(trialEndDate).toLocaleDateString()}` : 'On a trial'
    }
  }
  const top = monthlyTiers(catalog).at(-1)
  const premium = top ? top.id === tier : false
  return { label: name, tone: premium ? 'premium' : 'plan', Icon: premium ? Sparkles : BadgeCheck, title: `${name} package` }
}

/**
 * The active center's package, shown beside its name in the top bar. Owners, managers and admins
 * can click it to open Billing; everyone else sees it as a label.
 */
export const PackageBadge = () => {
  const history = useHistory()
  const ccId = useCenterStore((store) => store.ccId)
  const user = useUserStore((store) => store.currentUser)
  const entitlements = useEntitlementStore()
  const { catalog } = useCatalog()

  const loadedForThis =
    entitlements.loadedForCcId != null &&
    String(entitlements.loadedForCcId) === String(ccId) &&
    (!entitlements.loadedForUser || !user || entitlements.loadedForUser === entitlementUserKey(user))
  if (!loadedForThis || entitlements.failOpen || entitlements.loadFailed) return null

  const badge = packageBadgeOf(entitlements, catalog)
  const { Icon } = badge
  const canManage = Boolean(user && (user.is_owner || user.is_manager || user.is_admin))
  const className = `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[12px] font-semibold leading-5 ${TONES[badge.tone]}`
  const content = (
    <>
      <Icon className={`h-3.5 w-3.5 shrink-0 ${ICON_TONES[badge.tone]}`} aria-hidden='true' />
      <span className='truncate'>{badge.label}</span>
    </>
  )

  return canManage ? (
    <button
      type='button'
      onClick={() => history.push(BILLING_PATH)}
      title={`${badge.title} — open Billing`}
      aria-label={`Package: ${badge.label}. Open Billing`}
      className={`${className} max-w-[260px] transition-colors hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
    >
      {content}
    </button>
  ) : (
    <span title={badge.title} aria-label={`Package: ${badge.label}`} className={`${className} max-w-[260px]`}>
      {content}
    </span>
  )
}
